"use client";
import { ArrowRightIcon, ShoppingBasket } from "lucide-react";
import { Button } from "components/ui/button";
import { Drawer } from "components/ui/drawer";
import CustomerCartDrawer from "components/customer-cart-drawer";
import cart from "observables/cart";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface CustomerHeaderProps {
  onClickNext?: () => void;
  nextButtonText?: string;
  isReadOnly?: boolean;
  onClickEmptyCart?: () => void;
  onClickRemove?: (variantId: number) => void;
  onClickRemoveCombo?: (variantIds: Array<number>) => void;
  isAnimating?: boolean;
  disableNextButton?: boolean;
}

const CustomerHeader = ({
  onClickNext,
  nextButtonText = "Suivant",
  isReadOnly = false,
  onClickEmptyCart,
  onClickRemove,
  onClickRemoveCombo,
  isAnimating = false,
  disableNextButton = false,
}: CustomerHeaderProps) => {
  const [displayAmount, setDisplayAmount] = useState("");
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);

  // Calculate total directly from cart items
  const totalQuantity =
    (cart.items?.reduce((acc, current) => (acc += current.quantity), 0) || 0) +
    (cart.extras?.reduce((acc, current) => (acc += current.quantity), 0) || 0);

  // Update displayed amount whenever cart changes
  useEffect(() => {
    const amount = Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(cart.totalAfterVat - cart.totalPromotion);

    setDisplayAmount(amount);
  }, [cart.totalAfterVat, cart.totalPromotion, cart.items, cart.extras]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 p-4 z-50 bg-background flex flex-col lg:flex-row gap-2 shadow-md border-b">
        <div className="flex">
          <Button
            variant={"ghost"}
            className="h-6 mx-1"
            onClick={() => setIsCartDrawerOpen(true)}
          >
            <div className="relative cursor-pointer">
              <ShoppingBasket />

              <motion.div
                className="absolute top-[-0.65rem] right-[-0.65rem] rounded-full bg-red-600 text-slate-50 text-xs w-5 h-5 flex items-center justify-center"
                animate={
                  isAnimating
                    ? {
                        scale: [1, 1.5, 0.8, 1.2, 1],
                        rotate: [0, -15, 15, -10, 0],
                        transition: { duration: 0.35, ease: "easeInOut" },
                      }
                    : {}
                }
              >
                {totalQuantity}
              </motion.div>
            </div>
          </Button>

          <p className="ml-3">
            {"Tu payeras "}
            {displayAmount}
          </p>
        </div>

        <div className="flex-1 flex justify-end gap-2">
          <Button
            variant={"outline"}
            className="h-6 mx-1"
            onClick={() => setIsCartDrawerOpen(true)}
          >
            {"Voir le panier"}
          </Button>

          {onClickNext && (
            <Button
              onClick={onClickNext}
              disabled={disableNextButton}
              className="h-6 mx-1 flex items-center gap-1"
            >
              {nextButtonText}
              <ArrowRightIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      </header>
      {/* Add spacer div to prevent content from being hidden under the fixed header */}
      <div className="h-24 md:h-20"></div>
      <Drawer open={isCartDrawerOpen} onOpenChange={setIsCartDrawerOpen}>
        <CustomerCartDrawer
          onClickNext={onClickNext}
          isReadOnly={isReadOnly}
          onClickEmptyCart={onClickEmptyCart}
          onClickRemove={onClickRemove}
          onClickRemoveCombo={onClickRemoveCombo}
        />
      </Drawer>
    </>
  );
};

export default CustomerHeader;
